import { getApiBaseUrl } from "./configHelper";

const ACCESS_TOKEN_KEY = "AccessToken";
const REFRESH_TOKEN_KEY = "RefreshToken";
const VID_KEY = "Vid";
const USER_TYPE_KEY = "UserType";
const SESSION_EXPIRED_MESSAGE = "Your session has expired. Please log in again.";
const SESSION_EXPIRED_EVENT = "auth:session-expired";

let sessionExpiryHandled = false;

const getAccessToken = () =>
  localStorage.getItem(ACCESS_TOKEN_KEY) || localStorage.getItem("AuthToken");

const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);
const getStoredUserVid = (storage, key) => {
  try {
    return JSON.parse(storage.getItem(key) || "null")?.Vid ?? null;
  } catch (error) {
    console.warn(`Unable to read Vid from ${key}:`, error);
    return null;
  }
};

const getVid = () =>
  localStorage.getItem(VID_KEY) ||
  getStoredUserVid(localStorage, "tempUser") ||
  getStoredUserVid(localStorage, "user") ||
  getStoredUserVid(sessionStorage, "tempUser") ||
  getStoredUserVid(sessionStorage, "user");

const withVid = (payload = {}) => ({
  ...payload,
  Vid: payload?.Vid ?? getVid()
});

const setTokens = (data, vid = null) => {
  sessionExpiryHandled = false;

  const accessToken = data?.AccessToken || data?.AuthToken;

  if (accessToken) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem("AuthToken", accessToken);
  }

  if (data?.RefreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, data.RefreshToken);
  }

  if (vid) {
    localStorage.setItem(VID_KEY, vid);
  } else if (data?.Vid) {
    localStorage.setItem(VID_KEY, data.Vid);
  }

  localStorage.setItem(USER_TYPE_KEY, "Customer");
};

const clearTokens = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem("AuthToken");
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(VID_KEY);
  localStorage.removeItem(USER_TYPE_KEY);
};

const handleSessionExpired = () => {
  if (sessionExpiryHandled) return;
  sessionExpiryHandled = true;

  clearTokens();
  sessionStorage.setItem("sessionExpiredMessage", SESSION_EXPIRED_MESSAGE);

  if (typeof window === "undefined") return;

  const event = new CustomEvent(SESSION_EXPIRED_EVENT, {
    cancelable: true,
    detail: { message: SESSION_EXPIRED_MESSAGE }
  });

  window.dispatchEvent(event);

  if (!event.defaultPrevented) {
    window.location.href = "/";
  }
};

const authHeaders = () => {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const anonymousEndpoints = [
  "CustomerLoginApi",
  "PasswordLoginApi",
  "VerifyCustomerPasswordApi",
  "CustomerRegistrationApi",
  "GetCustAreaList",
  "GenerateOtp",
  "SendOtp",
  "VerifyOtp",
  "CheckPincode",
  "CreateRazorpayOrder",
  "VerifyPayment",
  "UpdateRazorPay",
  "RefreshToken",
  "GetHelpPages"
];

const isAnonymousEndpoint = (endpoint) => anonymousEndpoints.includes(endpoint);

const softAuthEndpoints = [];

const isSoftAuthEndpoint = (endpoint) => softAuthEndpoints.includes(endpoint);

const CACHEABLE_GET_ENDPOINTS = new Set([
  "GetMenuplan",
  "GetCustAreaList",
  "GetMultipleAdd",
  "GetIndividualCustomerList",
  "OrderHistory",
  "GetHelpPages",
  "GetContactUs"
]);

const GET_CACHE_DURATION_MS = 3000;
const getRequestCache = new Map();

const isCacheableGetEndpoint = (endpoint) => CACHEABLE_GET_ENDPOINTS.has(endpoint);

const clearGetRequestCache = () => {
  getRequestCache.clear();
};

let refreshPromise = null;
const refreshAccessToken = async () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const baseUrl = await getApiBaseUrl();
    const refreshToken = getRefreshToken();
    const vid = getVid();

    if (!refreshToken || !vid) {
      handleSessionExpired();
      return false;
    }

    try {
      const response = await fetch(`${baseUrl}/RefreshToken`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          Vid: Number(vid),
          RefreshToken: refreshToken
        })
      });

      if (response.status === 401 || response.status === 403) {
        handleSessionExpired();
        return false;
      }

      if (!response.ok) {
        console.error("Refresh server error:", response.status);
        return false;
      }

      const data = await response.json();

      if (data?.AccessToken || data?.AuthToken) {
        setTokens(data, vid);
        return true;
      }

      handleSessionExpired();
      return false;
    } catch (e) {
      console.error("Refresh token network/server error:", e);
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

const fetchWithRefresh = async (url, options, endpoint) => {
  let response = await fetch(url, options);

  if (
    response.status === 401 &&
    !isAnonymousEndpoint(endpoint) &&
    !isSoftAuthEndpoint(endpoint)
  ) {
    const refreshed = await refreshAccessToken();

    if (refreshed) {
      response = await fetch(url, {
        ...options,
        headers: {
          ...(options.headers || {}),
          ...authHeaders()
        }
      });
    }
  }

  return response;
};

const api = {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
  refreshAccessToken,

  get: async (endpoint, params = {}) => {
    const baseUrl = await getApiBaseUrl();
    const url = new URL(`${baseUrl}/${endpoint}`);

    Object.keys(params).forEach((key) => {
      if (params[key] !== undefined && params[key] !== null) {
        url.searchParams.append(key, params[key]);
      }
    });

    const requestUrl = url.toString();
    const cacheable = isCacheableGetEndpoint(endpoint);
    const cached = cacheable ? getRequestCache.get(requestUrl) : null;
    const now = Date.now();

    if (cached?.data && cached.expiresAt > now) {
      return cached.data;
    }

    if (cached?.promise) {
      return cached.promise;
    }

    const requestPromise = (async () => {
      const response = await fetchWithRefresh(
        requestUrl,
        {
          method: "GET",
          headers: isAnonymousEndpoint(endpoint) ? {} : authHeaders()
        },
        endpoint
      );

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();

      if (cacheable) {
        getRequestCache.set(requestUrl, {
          data,
          expiresAt: Date.now() + GET_CACHE_DURATION_MS
        });
      }

      return data;
    })();

    if (cacheable) {
      getRequestCache.set(requestUrl, { promise: requestPromise, expiresAt: 0 });
    }

    try {
      return await requestPromise;
    } catch (error) {
      if (cacheable) getRequestCache.delete(requestUrl);
      throw error;
    }
  },

  post: async (endpoint, body = {}, extraParams = {}) => {
    const baseUrl = await getApiBaseUrl();
    const url = new URL(`${baseUrl}/${endpoint}`);

    Object.keys(extraParams).forEach((key) => {
      if (extraParams[key] !== undefined && extraParams[key] !== null) {
        url.searchParams.append(key, extraParams[key]);
      }
    });

    const headers = isAnonymousEndpoint(endpoint)
      ? { "Content-Type": "application/json" }
      : { "Content-Type": "application/json", ...authHeaders() };

    const response = await fetchWithRefresh(
      url.toString(),
      {
        method: "POST",
        headers,
        body: JSON.stringify(body)
      },
      endpoint
    );

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    clearGetRequestCache();
    return response.json();
  },

  CustomerRegistrationApi: (payload) =>
    api.post("CustomerRegistrationApi", payload),

  CustomerMobileAddAPI: (payload) =>
    api.post("CustomerMobileAddAPI", payload),

  SaveOnlineOrdersAPI: (payload) =>
    api.post("SaveOnlineOrdersAPI", payload),

 CustomerLoginApi: async (Vcode) => {
  const result = await api.get("CustomerLoginApi", { Vcode });
  const vid = result?.CustomerLoginApijs?.Table?.[0]?.Vid;
  if (vid !== undefined && vid !== null) {
    localStorage.setItem(VID_KEY, vid);
  }
  return result;
},

 PasswordLoginApi: async (Vcode, VPassword, Vid) => {
  const result = await api.get(
    "PasswordLoginApi",
    withVid({ Vcode, VPassword, Vid })
  );
  return result;
},

  VerifyCustomerPasswordApi: async (Vcode, VPassword) => {
    const result = await api.get("VerifyCustomerPasswordApi", { Vcode, VPassword });

    const vid =
      result?.Vid ||
      result?.VerifyCustomerPasswordApijs?.Table?.[0]?.Vid ||
      result?.PasswordLoginApijs?.Table?.[0]?.Vid;

    setTokens(result, vid);
    return result;
  },

  GetMenuplan: (MenuDate) =>
    api.get("GetMenuplan", withVid({ MenuDate })),

  GetCustAreaList: () =>
    api.get("GetCustAreaList"),

  GetMultipleAdd: (Vid) =>
    api.get("GetMultipleAdd", { Vid }),

  GetIndividualCustomerList: (Vid, UserType) =>
    api.get("GetIndividualCustomerList", { Vid, UserType }),

  UpdateProfile: (payload) =>
    api.post("UpdateProfile", withVid(payload)),

  OrderHistory: (Vid, UserType) =>
    api.get("OrderHistory", { Vid, UserType }),

  OrderRatingsFeedback: (Vid, OId, Ratings, Feedback) =>
    api.get("OrderRatingsFeedback", { Vid, OId, Ratings, Feedback }),

  GetHelpPages: () =>
    api.get("GetHelpPages"),

  GetContactUs: (User = "ADMIN") =>
    api.get("GetContactUs", { User }),

  GenerateOtp: (Vcode, UserType, Vid) =>
    api.get("GenerateOtp", withVid({ Vcode, UserType, Vid })),

  SendOtp: (payload) =>
    api.post("SendOtp", payload),

  VerifyOtp: (Vcode, UserType, Otp, Vid) =>
    api.get("VerifyOtp", withVid({ Vcode, UserType, Otp, Vid })),

  CheckPincode: (Pincode) =>
    api.get("CheckPincode", { Pincode }),

  GetDeleteCustomerAdd: (Vid, AId, Action) =>
    api.get("GetDeleteCustomerAdd", { Vid, AId, Action }),

  CreateRazorpayOrder: (payload) =>
    api.post("CreateRazorpayOrder", payload),

  VerifyPayment: (payload) =>
    api.post("VerifyPayment", payload),

  UpdateRazorPay: (payload) =>
    api.post("UpdateRazorPay", withVid(payload)),

  RefreshToken: () =>
    refreshAccessToken(),

CustomerLogout: async () => {
  const vid = getVid();
  const refreshToken = getRefreshToken();

  try {
    if (vid && refreshToken) {
      await api.post("CustomerLogout", {
        Vid: Number(vid),
        RefreshToken: refreshToken
      });
    }
  } catch (e) {
    console.error("Logout API failed:", e);
  }

  clearTokens();
}
};

api.UploadAttachment = async (formData) => {
  const baseUrl = await getApiBaseUrl();
  const url = `${baseUrl}/api/UploadAttachment`;

  const response = await fetchWithRefresh(
    url,
    {
      method: "POST",
      headers: authHeaders(),
      body: formData
    },
    "UploadAttachment"
  );

  if (!response.ok) throw new Error(`Upload failed! ${response.status}`);
  return response.json();
};

api.GetAttachments = async (MdlId, PrimeId) => {
  const baseUrl = await getApiBaseUrl();
  const url = new URL(`${baseUrl}/api/GetAttachmentsImage`);

  url.searchParams.append("mdlId", MdlId);
  if (PrimeId !== undefined && PrimeId !== null && PrimeId !== "") {
    url.searchParams.append("primeId", PrimeId);
  }

  const response = await fetchWithRefresh(
    url.toString(),
    {
      method: "GET",
      headers: authHeaders()
    },
    "GetAttachments"
  );

  if (!response.ok) throw new Error("Failed to fetch attachments");
  return response.json();
};

api.DeleteAttachment = async (id) => {
  const baseUrl = await getApiBaseUrl();
  const url = new URL(`${baseUrl}/api/DeleteAttachment`);

  url.searchParams.append("id", id);

  const response = await fetchWithRefresh(
    url.toString(),
    {
      method: "DELETE",
      headers: authHeaders()
    },
    "DeleteAttachment"
  );

  if (!response.ok) throw new Error("Failed to delete attachment");
  return response.json();
};

export { SESSION_EXPIRED_EVENT, SESSION_EXPIRED_MESSAGE };

export default api;
